/*
 * Оболочка «Кубикон CAD» — заменила Main.jsx сайта робототехники.
 *
 * ЗАЧЕМ. Раньше на cad.minecraftia-school.ru открывался ВЕСЬ старый сайт: меню
 * с электроникой, микроконтроллерами, редактором схем, битвой рейтингов, чужой
 * логотип «RoboТехника» и подвал «заходи посмотреть на программирование». Домен
 * же существует ради одного продукта — 3D-редактора. Поэтому здесь только он:
 * свои проекты, лента сообщества и (позже) курсы по 3D-моделированию.
 *
 * Дизайн — токены Майнкрафтии 2.0 (shell.css), но в тёмной схеме: сам редактор
 * тёмный, светлая оболочка вокруг тёмного вьюпорта резала бы глаз.
 */
import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';

import KubikonCADPage from '../KubikonCAD/KubikonCADPage';
import CadProjectCardPage from '../../components/KubikonCAD/CadProjectCardPage';
import CoursesPage from '../../components/KubikonCAD/lesson/CoursesPage';
import { AvatarFrame } from '../../components/AvatarFrame/AvatarFrame';
import UserService from '../../API/UserService';
import { FOLDER_PROFILE } from '../../API/API';
import { IconCube, IconFolder, IconFeed, IconBook, IconBack, IconCollapse } from './icons';
import './shell.css';

const SCHOOL_URL = 'https://minecraftia-school.ru/';

// Пункты меню. Всё, что не про 3D, убрано: единственный источник правды на этом
// домене — редактор. «Курсы» — заготовка под курсы по 3D-моделированию.
const NAV = [
    { to: '/main/cad-editor', label: 'Мои проекты', Icon: IconFolder, end: true },
    { to: '/main/cad-projects/feed', label: 'Лента сообщества', Icon: IconFeed },
    { to: '/main/cad-courses', label: 'Курсы', Icon: IconBook },
];


/*
 * Маршруты вынесены в ОТДЕЛЬНЫЙ memo-компонент — это принципиально.
 *
 * Пока <Routes> жили прямо в теле CadShell, любое изменение состояния оболочки
 * (свернулось меню, подгрузился профиль, открылось мобильное меню) перерисовывало
 * ВСЁ поддерево, включая редактор. React при этом пересоздаёт узлы вокруг живого
 * WebGL-канваса, и сыпалось
 * «NotFoundError: Failed to execute 'removeChild'» + «Context Lost» — редактор
 * падал ровно в момент, когда открывалась панель свойств (выбор инструмента
 * эскиза), потому что панель добавляется соседом канваса.
 *
 * memo без пропсов = поддерево не перерисовывается вообще: маршрутизацию ведёт
 * сам react-router по своему контексту.
 */
const CadRoutes = React.memo(function CadRoutes() {
    return (
        <Routes>
            <Route path="cad-editor" element={<KubikonCADPage />} />
            <Route path="cad-editor/new" element={<KubikonCADPage isNew />} />
            <Route path="cad-editor/:id" element={<KubikonCADPage />} />
            <Route path="cad-projects/feed" element={<KubikonCADPage feedTab />} />
            <Route path="cad-projects/:id" element={<CadProjectCardPage />} />
            <Route path="cad-courses" element={<CoursesPage />} />

            {/* Всё прочее — в редактор: других разделов на домене нет. */}
            <Route path="*" element={<Navigate to="/main/cad-editor" replace />} />
        </Routes>
    );
});


/** Активен ли пункт меню для текущего пути.
 *  end=true — только точное совпадение (иначе «Мои проекты» горели бы вместе
 *  с открытым проектом /cad-editor/42). */
function isActivePath(pathname, to, end) {
    return end ? pathname === to : pathname.startsWith(to);
}


/** Шапка: название раздела слева, профиль справа. */
function CadHeader({ profile, onBurger }) {
    const { pathname } = useLocation();

    const title = pathname.includes('/cad-projects/feed') ? 'Лента сообщества'
        : pathname.includes('/cad-courses') ? 'Курсы'
        : pathname.includes('/cad-projects/') ? 'Проект'
        : 'Мои проекты';

    const name = profile?.firstName
        ? `${profile.firstName}${profile.lastName?.trim() ? ' ' + profile.lastName : ''}`
        : 'Профиль';

    const photo = profile?.photo ? FOLDER_PROFILE + profile.photo : null;

    return (
        <header className="cadHeader">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="cadBurger" onClick={onBurger} aria-label="Меню">
                    <IconCollapse size={18} />
                </button>
                <span className="cadTitle">{title}</span>
            </div>

            <div className="cadUser">
                {/* Рамка аватара — та же косметика, что в школе (CSS, не картинка).
                    Старая шапка робототехники её не знала, поэтому у МИНа рамка
                    «galaxy» не отображалась вовсе. */}
                <div className="cadUserMeta" style={{ textAlign: 'right' }}>
                    <span className="cadUserName">{name}</span>
                    {profile?.level != null && (
                        <span className="cadUserLevel">{profile.level} уровень</span>
                    )}
                </div>
                <AvatarFrame frame={profile?.active_frame || 'none'} size={40} thickness={3}>
                    {photo
                        ? <img className="cadAvatarImg" src={photo} alt="" />
                        : <div className="cadAvatarImg" />}
                </AvatarFrame>
            </div>
        </header>
    );
}


export default function CadShell() {
    const [collapsed, setCollapsed] = useState(false);
    const [open, setOpen] = useState(false);        // мобильное меню
    const [profile, setProfile] = useState(null);
    const { pathname } = useLocation();

    /*
     * В самом редакторе меню сворачивается автоматически.
     *
     * Там свои панели (обозреватель, лента инструментов, хронология) и модель
     * во весь экран — широкий сайдбар с подписями отнимает у неё место и
     * выглядит лишним. Прятать совсем нельзя: пропадёт путь назад к проектам.
     * Поэтому сворачиваем до иконок, а на списке проектов разворачиваем обратно.
     *
     * Ручной выбор пользователя при этом уважаем: как только он сам щёлкнул
     * по кнопке, автоматика больше не вмешивается (userChoice).
     */
    const inEditor = /\/main\/cad-editor\/(new|\d+)/.test(pathname);
    const [userChoice, setUserChoice] = useState(false);

    /*
     * Меняем состояние сайдбара в СЛЕДУЮЩЕМ кадре, а не синхронно со сменой
     * роута. Иначе React одновременно размонтирует канвас редактора и
     * перестраивает сайдбар — и валится
     * «NotFoundError: Failed to execute 'removeChild'», а WebGL теряет контекст.
     * Один requestAnimationFrame разводит эти две перестройки по разным кадрам.
     */
    useEffect(() => {
        if (userChoice) return;
        const id = requestAnimationFrame(() => setCollapsed(inEditor));
        return () => cancelAnimationFrame(id);
    }, [inEditor, userChoice]);

    useEffect(() => {
        let alive = true;
        UserService.getMyProfile()
            .then((d) => { if (alive) setProfile(d); })
            .catch(() => { /* профиль не критичен: редактор работает и без него */ });
        return () => { alive = false; };
    }, []);

    return (
        <div className="cadShell" data-editor={inEditor || undefined}>
            <aside className="cadSidebar" data-collapsed={collapsed} data-open={open}>
                <a className="cadBrand" href={SCHOOL_URL} title="Вернуться в Майнкрафтию">
                    <IconCube size={30} />
                    <span className="cadBrandText">
                        <span className="cadBrandName">Кубикон CAD</span>
                        <span className="cadBrandSub">Майнкрафтия</span>
                    </span>
                </a>

                <nav className="cadNav">
                    <div className="cadNavLabel">{collapsed ? '3D' : 'Редактор'}</div>

                    {NAV.map(({ to, label, Icon, soon, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            className="cadNavItem"
                            // Подсветку активного пункта отдаём CSS через data-атрибут:
                            // так стиль живёт в одном месте, а не размазан по JSX.
                            data-soon={soon || undefined}
                            data-active={!soon && isActivePath(pathname, to, end) ? 'true' : undefined}
                            onClick={(e) => {
                                if (soon) { e.preventDefault(); return; }   // раздела ещё нет
                                setOpen(false);
                            }}
                        >
                            <span className="cadNavIcon"><Icon size={20} /></span>
                            <span className="cadNavText">{label}</span>
                            {soon && <span className="cadSoonMark">скоро</span>}
                        </NavLink>
                    ))}

                    <div className="cadNavLabel">{collapsed ? '←' : 'Школа'}</div>
                    <a className="cadNavItem" href={SCHOOL_URL}>
                        <span className="cadNavIcon"><IconBack size={20} /></span>
                        <span className="cadNavText">В Майнкрафтию</span>
                    </a>
                </nav>

                <button
                    className="cadCollapse"
                    onClick={() => { setUserChoice(true); setCollapsed((v) => !v); }}
                    title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
                >
                    <IconCollapse size={18} flip={collapsed} />
                </button>
            </aside>

            <div className="cadMain">
                <CadHeader profile={profile} onBurger={() => setOpen((v) => !v)} />

                <div className="cadContent" data-editor={inEditor || undefined}>
                    <CadRoutes />
                </div>
            </div>
        </div>
    );
}
